// src/services/sursysLogisticAPI.js
//
// Real-data implementation backed by the surrounding-system (sursys) Go backend,
// which fronts synced Accurate ERP data. Drop-in replacement for mockLogisticAPI:
// same function signatures, same return shapes.
//
// Endpoints require company_id + system_name. We default to the SUMA tenant.
// See memory/sursys-backend.md for the full API contract.
//
// NOTE on data coverage: Accurate's synced tables give us REAL stock per item
// and REAL stock per branch/warehouse. They do NOT carry a per-branch
// "minimum stock" or "demand/order" figure, so those are derived as transparent
// proxies (see deriveMinStock / demand below) until a real source is wired.

import { surroundingSys } from './api';
import { lookupJobsBatch } from './gapfamilyAPI';

// ── Tenant ────────────────────────────────────────────────────────────────
export const TENANTS = {
  SUMA: { company_id: 'cabcb4a6-2d47-4f5e-b85d-5be8d4ab61a1', system_name: 'accurate', label: 'PT. Suma Alpha Indonesia' },
  GP:   { company_id: 'aa2c63cf-3230-48e0-bbb6-bfe68542e14c', system_name: 'accurate', label: 'PT. Gelora Printing' },
};

let activeTenant = TENANTS.SUMA;
export const setActiveTenant = (key) => { if (TENANTS[key]) activeTenant = TENANTS[key]; };

const tenantParams = () => ({ company_id: activeTenant.company_id, system_name: activeTenant.system_name });

// Accurate values arrive as strings; coerce safely.
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const formatMillions = (v) => {
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} K`;
  return `${v}`;
};

// "Gudang SUMA Medan" → { id: "MEDAN", name: "Medan" }
const branchFromWarehouse = (whName = '') => {
  const city = whName.replace(/gudang/i, '').replace(/suma/i, '').replace(/[\[\]]/g, '').trim() || whName.trim();
  return { id: city.toUpperCase().slice(0, 6) || 'NA', name: city || whName };
};

// ── Shared: fetch top items + their per-branch (warehouse) stock ─────────────
// Returns [{ id, name, no, unit, category, totalStock, availableToSell,
//            branches: [{ branchId, name, stock }] }]
const fetchItemsWithBranches = async (count = 6) => {
  const { data } = await surroundingSys.get('/stock', { params: { ...tenantParams(), limit: 100 } });
  const rows = data?.data || [];

  // Prioritise items most "at risk": lowest available-to-sell first, but skip dead zeros.
  const picked = rows
    .map((r) => ({
      no: r.ITEM_STOCK_NO,
      name: r.ITEM_STOCK_NAME || r.ITEM_STOCK_NO,
      unit: r.ITEM_STOCK_UNIT || 'pcs',
      category: r.ITEM_STOCK_CATEGORY_INDENT || '-',
      totalStock: num(r.ITEM_STOCK_ALL_BRANCH),
      availableToSell: num(r.ITEM_STOCK_AVAILABLE_TO_SELL),
    }))
    .sort((a, b) => a.availableToSell - b.availableToSell)
    .slice(0, count);

  const withBranches = await Promise.all(
    picked.map(async (item) => {
      try {
        const res = await surroundingSys.get(`/stock/${encodeURIComponent(item.no)}/warehouses`, {
          params: { ...tenantParams(), limit: 100 },
        });
        const whs = res.data?.data || [];
        const branches = whs
          // Keep only the company's own branch warehouses ("Gudang ..."),
          // not consignment/customer locations (Koperasi, PT, Toko, etc).
          .filter((w) => /gudang/i.test(w.WAREHOUSE_NAME || w.WAREHOUSE_WAREHOUSE_NAME || ''))
          .map((w) => {
            const b = branchFromWarehouse(w.WAREHOUSE_NAME || w.WAREHOUSE_WAREHOUSE_NAME);
            return { branchId: b.id, name: b.name, stock: num(w.WAREHOUSE_BALANCE_ITEM ?? w.WAREHOUSE_UNIT1_QUANTITY) };
          });
        return { ...item, id: item.no, branches };
      } catch {
        return { ...item, id: item.no, branches: [] };
      }
    })
  );

  return withBranches.filter((i) => i.branches.length > 0);
};

// Proxy minimum stock: 20% of an item's total stock spread across its branches.
const deriveMinStock = (item, branchCount) =>
  Math.max(1, Math.round((item.totalStock * 0.2) / Math.max(1, branchCount)));

// ── Production deficit: open Sales Orders − current stock (REAL demand) ───────
// Powers the "what to produce" signal. deficit > 0 → perlu diproduksi.
export const fetchProductionDeficit = async ({ onlyDeficit = true, limit = 50, search = '' } = {}) => {
  const { data } = await surroundingSys.get('/production/deficit', {
    params: { ...tenantParams(), only_deficit: onlyDeficit, limit, search },
  });
  return (data?.data || []).map((r) => ({
    itemNo: r.item_no,
    name: r.item_name || r.item_no,
    unit: r.unit || 'pcs',
    category: r.category || '-',
    demand: num(r.demand_qty),
    shipped: num(r.shipped_qty),
    outstanding: num(r.outstanding),
    currentStock: num(r.current_stock),
    deficit: num(r.deficit),
  }));
};

// ── Top 10 produk by total stock value ──────────────────────────────────────
export const fetchRegionStock = async () => {
  const { data } = await surroundingSys.get('/stock', { params: { ...tenantParams(), limit: 100 } });
  const rows = data?.data || [];

  return rows
    .map((r) => ({ name: r.ITEM_STOCK_NAME || r.ITEM_STOCK_NO, _value: num(r.ITEM_STOCK_BALANCE_TOTAL_STOCK) }))
    .sort((a, b) => b._value - a._value)
    .slice(0, 10)
    .map((r) => ({
      name: r.name.length > 22 ? `${r.name.slice(0, 20)}...` : r.name,
      value: +(r._value / 1_000_000).toFixed(2),
      formatted: formatMillions(r._value),
    }));
};

// ── Distribution matrix: real stock per branch ───────────────────────────────
export const fetchDistributionMatrix = async () => {
  const items = await fetchItemsWithBranches(6);

  // Ambil data produksi per-cabang (REAL) untuk mengisi Pesanan & Serapan.
  // /production/dashboard mengembalikan cabang[branch] = { sp_belum, keluar_tahunan, ... }.
  // Dibuat map: kode -> { branchNameLower -> { sp_belum, keluar_tahunan } }.
  let prodByCode = {};
  try {
    const { data } = await surroundingSys.get('/production/dashboard', {
      params: { ...tenantParams(), limit: 500 },
    });
    (data?.items || []).forEach((it) => {
      const perBranch = {};
      Object.entries(it.cabang || {}).forEach(([branch, m]) => {
        perBranch[branch.toLowerCase()] = {
          sp_belum: num(m.sp_belum),
          serapan: num(m.keluar_tahunan),
        };
      });
      if (it.kode != null) prodByCode[String(it.kode).trim()] = perBranch;
    });
  } catch (e) {
    console.warn('fetchDistributionMatrix: data produksi per-cabang tidak tersedia:', e?.message);
  }

  // WIP (status produksi) per-produk dari gapfamily (skala nasional, bukan per-cabang).
  let wipByCode = {};
  try {
    wipByCode = await lookupJobsBatch(items.map((i) => i.no));
  } catch (e) {
    console.warn('fetchDistributionMatrix: WIP gapfamily tidak tersedia:', e?.message);
  }

  // Union of all branches so every row shares the same columns (widget renders
  // headers from the first row). Missing branch on an item → stock 0.
  const branchOrder = [];
  const seen = new Set();
  items.forEach((it) =>
    it.branches.forEach((b) => {
      if (!seen.has(b.branchId)) { seen.add(b.branchId); branchOrder.push({ branchId: b.branchId, name: b.name }); }
    })
  );

  return items.map((item) => {
    const minStock = deriveMinStock(item, branchOrder.length);
    const byId = Object.fromEntries(item.branches.map((b) => [b.branchId, b.stock]));
    const prod = prodByCode[String(item.no).trim()] || {};
    // WIP nasional: qty di workcenter berjalan (atau true bila masih dalam proses).
    const job = wipByCode[String(item.no).trim()] || wipByCode[String(item.no).replace(/\D/g, '')];
    const nationalWip = job && !job.selesai ? num(job?.proses_saat_ini?.output) : 0;
    return {
      id: item.id,
      name: item.name,
      // WIP bersifat nasional (di pabrik), tidak terpecah per-cabang — disimpan di level item.
      nationalWip,
      wipLabel: job?.wip_isi || null,
      noJob: job?.no_job || null,
      branches: branchOrder.map((b) => {
        const p = prod[b.name.toLowerCase()] || {};
        return {
          branchId: b.branchId,
          name: b.name,
          stock: byId[b.branchId] ?? 0,
          wip: 0,                                   // WIP tidak ada per-cabang (lihat nationalWip di level item)
          transit: 0,                               // belum ada sumber data in-transit antar-cabang
          minStock,                                 // proxy (see deriveMinStock)
          demand: p.sp_belum ?? minStock,           // REAL: pesanan terbuka (SP belum kirim) per cabang
          lastYearSales: p.serapan ?? 0,            // REAL: serapan (keluar_tahunan) per cabang
        };
      }),
    };
  });
};

// ── Intelligent restock analysis: REAL demand (open SO) vs REAL stock ────────
// Demand & deficit come from /production/deficit; per-branch stock from the
// warehouse endpoint decides MUTASI (ada cabang surplus) vs PRODUKSI SEGERA.
export const fetchIntelligentStockAnalysis = async () => {
  const deficits = await fetchProductionDeficit({ onlyDeficit: true, limit: 6 });

  return Promise.all(
    deficits.map(async (d) => {
      // Per-branch stock for this item (own gudang only).
      let branches = [];
      try {
        const res = await surroundingSys.get(`/stock/${encodeURIComponent(d.itemNo)}/warehouses`, {
          params: { ...tenantParams(), limit: 100 },
        });
        branches = (res.data?.data || [])
          .filter((w) => /gudang/i.test(w.WAREHOUSE_NAME || w.WAREHOUSE_WAREHOUSE_NAME || ''))
          .map((w) => {
            const b = branchFromWarehouse(w.WAREHOUSE_NAME || w.WAREHOUSE_WAREHOUSE_NAME);
            return { branch: b.name, stock: num(w.WAREHOUSE_BALANCE_ITEM ?? w.WAREHOUSE_UNIT1_QUANTITY) };
          })
          .sort((a, b) => b.stock - a.stock);
      } catch { /* keep national-only view */ }

      // A donor needs meaningful surplus (covers at least 10% of the deficit).
      const donor = branches.find((b) => b.stock > 0 && b.stock >= d.deficit * 0.1);

      let recommendation;
      if (d.deficit <= 0) {
        recommendation = { action: 'HOLD', source: null, urgency: 'SAFE', notes: 'Stok cukup untuk pesanan terbuka' };
      } else if (donor) {
        recommendation = {
          action: 'MUTASI', source: donor.branch, urgency: 'WARNING',
          notes: `Defisit ${d.deficit.toLocaleString()} — sebagian bisa ditutup dari ${donor.branch}`,
        };
      } else {
        recommendation = {
          action: 'PRODUKSI SEGERA', source: 'Pabrik', urgency: 'CRITICAL',
          notes: `Perlu produksi ${d.deficit.toLocaleString()} ${d.unit} (pesanan ${d.outstanding.toLocaleString()} vs stok ${d.currentStock.toLocaleString()})`,
        };
      }

      return {
        id: d.itemNo,
        name: d.name,
        branch: 'Nasional',
        currentStock: d.currentStock,
        minStock: d.outstanding,     // "minimum" = pesanan terbuka yang harus dipenuhi
        yoySalesTrend: 0,            // YoY source not wired yet
        wipProduction: 0,
        inTransit: 0,
        otherBranchStock: branches,
        recommendation,
      };
    })
  );
};

// ── Not yet migrated (need geo / production sources) — fall back to mock ──────
export {
  fetchProductionData,
  fetchDeliveryStatus,
  fetchSavedDistributionPlans,
  saveDistributionPlan,
  deleteDistributionPlan,
} from './mockLogisticAPI';
